import React, { useState, useEffect } from 'react';
import { savePerformanceResponse, getPerformanceResponses } from '../../../../../services/performanceService';
import { startOfWeek, endOfWeek } from 'date-fns';
import PerformanceChart from '../../../Share/PerformanceChart';
import './PerformanceQuestion.css';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const PerformanceQuestion = ({ userId = null, selectedTimestamp = null, onClose = null }) => {
    const [score, setScore] = useState(5);
    const [responses, setResponses] = useState([]);
    const [tab, setTab] = useState('question');
    const [currentResponse, setCurrentResponse] = useState(null);
    const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
    const { t, i18n } = useTranslation();

    useEffect(() => {
        if (userId) {
            fetchResponses();
        }
    }, [userId, currentWeek, selectedTimestamp]);

    const fetchResponses = async () => {
        try {
            const res = await getPerformanceResponses(userId);

            // Filter responses for the current week
            const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
            const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });
            const filteredResponses = res.data.filter((response) => {
                const responseTime = new Date(response.timestamp);
                return responseTime >= weekStart && responseTime <= weekEnd;
            });

            setResponses(filteredResponses);

            // Check for response matching the selected timestamp (or current time)
            const selectedTime = new Date(selectedTimestamp || new Date());
            const startOfHour = new Date(
                selectedTime.getFullYear(),
                selectedTime.getMonth(),
                selectedTime.getDate(),
                selectedTime.getHours()
            );
            const endOfHour = new Date(startOfHour.getTime() + 60 * 60 * 1000);

            const responseForSelectedTime = filteredResponses.find((response) => {
                const responseTime = new Date(response.timestamp);
                return responseTime >= startOfHour && responseTime < endOfHour;
            });

            setCurrentResponse(responseForSelectedTime);
        } catch (error) {
            console.error('Error fetching performance responses:', error);
        }
    };

    const handleSaveResponse = async () => {
        try {
            const timestamp = selectedTimestamp || new Date().toISOString();
            await savePerformanceResponse(userId, { score, timestamp });
            alert(t('performanceQuestion.responseSaved'));
            if (userId) fetchResponses(); // Refresh responses only if userId is available
            if (onClose) onClose(); // Close the modal if callback provided
        } catch (error) {
            console.error('Error saving performance response:', error);
            alert(t('performanceQuestion.responseSaveError'));
        }
    };

    const getScaleExplanation = () => {
        const isGerman = i18n.language === 'de';

        if (isGerman) {
            return {
                title: "Wie sollte ich meine Leistung bewerten?",
                intro: "Die Selbsteinschätzung der Leistung auf einer Skala von 1–10 bedeutet, dass Ihre mentale und/oder psychologische Fähigkeit, Aufgaben in diesem Moment zu erfüllen, zwischen sehr niedrig (1) und sehr hoch (10) nach Ihrer persönlichen Wahrnehmung bewertet werden sollte.",
                scale: [
                    { value: 1, text: "Extrem niedrige Leistungsfähigkeit – kaum in der Lage, Aufgaben oder Stress zu bewältigen. Selbst kleinste Anforderungen sind überwältigend." },
                    { value: 2, text: "Sehr niedrige Leistungsfähigkeit – nur minimale Leistung möglich, schnelle Erschöpfung, kaum Belastbarkeit." },
                    { value: 3, text: "Deutlich eingeschränkte Leistungsfähigkeit – nur einfache Tätigkeiten möglich, mittlere oder längere Anforderungen können nicht aufrechterhalten werden." },
                    { value: 4, text: "Unterdurchschnittliche Leistung – in der Lage, alltägliche Aufgaben zu bewältigen, aber erreicht Grenzen auch bei mäßigem Stress." },
                    { value: 5, text: "Durchschnittliche Leistung – normale alltägliche Aufgaben sind möglich, aber spürbare Müdigkeit oder Überlastung tritt bei höherem Stress auf." },
                    { value: 6, text: "Leicht überdurchschnittliche Leistung – gute Belastbarkeit, auch bei längeren Aufgaben, aber mit Einschränkungen bei sehr hohen Anforderungen." },
                    { value: 7, text: "Gute Leistung – überdurchschnittliche Belastbarkeit, die meisten Aufgaben können erfolgreich und ohne große Erschöpfung gemeistert werden." },
                    { value: 8, text: "Sehr gute Leistung – hohe Belastbarkeit, auch längere oder anstrengende Aktivitäten können gut gemeistert werden." },
                    { value: 9, text: "Nahe maximale Leistung – außergewöhnliche Belastbarkeit, sehr seltene Müdigkeit, Spitzenleistung erreichbar." },
                    { value: 10, text: "Maximale Leistung – bestmögliche Leistung, optimale körperliche, mentale und psychologische Belastbarkeit." }
                ]
            };
        } else {
            return {
                title: "How should I rate my performance?",
                intro: "Self-assessment of performance on a scale of 1–10 means that your mental and/or psychological ability to perform tasks in that moment should be rated between very low (1) and very high (10) according to your personal perception.",
                scale: [
                    { value: 1, text: "Extremely low performance capacity – barely able to cope with tasks or stress. Even the smallest demands are overwhelming." },
                    { value: 2, text: "Very low performance capacity – only minimal performance possible, rapid exhaustion, hardly any resilience." },
                    { value: 3, text: "Significantly limited performance capacity – only simple activities possible, medium or longer demands cannot be sustained." },
                    { value: 4, text: "Below-average performance – able to cope with everyday tasks, but reaches limits even with moderate stress." },
                    { value: 5, text: "Average performance – normal everyday tasks are possible, but noticeable fatigue or overload occurs with higher stress." },
                    { value: 6, text: "Slightly above-average performance – good resilience, even with longer tasks, but with limitations when demands are very high." },
                    { value: 7, text: "Good performance – above-average resilience, most tasks can be mastered successfully and without great exhaustion." },
                    { value: 8, text: "Very good performance – high resilience, even longer or strenuous activities can be mastered well." },
                    { value: 9, text: "Near maximum performance – exceptional resilience, very rare fatigue, peak performance achievable." },
                    { value: 10, text: "Maximum performance – best possible performance, optimal physical, mental and psychological resilience." }
                ]
            };
        }
    };

    const renderQuestionTab = () => {
        const explanation = getScaleExplanation();

        return (
            <div className="tab-content">
                <h3>{t('performanceQuestion.questionTitle')}</h3>

                {/* Scale Explanation Accordion */}
                <Accordion sx={{ mb: 2, mt: 2 }}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="scale-explanation-content"
                        id="scale-explanation-header"
                    >
                        <Typography sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                            {explanation.title}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                            {explanation.intro}
                        </Typography>
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {explanation.scale.map((item) => (
                                <Typography
                                    key={item.value}
                                    variant="body2"
                                    sx={{
                                        mb: 1.5,
                                        pl: 2,
                                        borderLeft: '3px solid #1976d2',
                                        paddingLeft: '12px'
                                    }}
                                >
                                    <strong>{item.value}</strong> = {item.text}
                                </Typography>
                            ))}
                        </div>
                    </AccordionDetails>
                </Accordion>

                {currentResponse ? (
                    <div>
                        <p>
                            {t('performanceQuestion.alreadySubmitted', {
                                time: new Date(currentResponse.timestamp).toLocaleTimeString(),
                            })}
                        </p>
                        <p>
                            {t('performanceQuestion.score')}: {currentResponse.score}
                        </p>
                    </div>
                ) : (
                    <div>
                        <input
                            type="range"
                            min="0"
                            max="10"
                            value={score}
                            onChange={(e) => setScore(e.target.value)}
                            className="slider"
                        />
                        <p>
                            {t('performanceQuestion.score')}: {score}
                        </p>
                        <button onClick={handleSaveResponse} className="save-button">
                            {t('dailySurvey.save')}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const renderHistoryTab = () => (
        <div className="tab-content">
            <h3>{t('performanceQuestion.responseHistory')}</h3>
            {responses.map((response, index) => (
                <li key={index}>
                    {new Date(response.timestamp).toLocaleString()}: {response.score}
                </li>
            ))}
        </div>
    );

    return (
        <div className="performance-question">
            <div className="tabs">
                <button
                    onClick={() => setTab('question')}
                    className={tab === 'question' ? 'active' : ''}
                >
                    {t('performanceQuestion.questionTab')}
                </button>
                <button
                    onClick={() => setTab('history')}
                    className={tab === 'history' ? 'active' : ''}
                >
                    {t('performanceQuestion.historyTab')}
                </button>
                <button
                    onClick={() => setTab('chart')}
                    className={tab === 'chart' ? 'active' : ''}
                >
                    {t('performanceQuestion.chartTab')}
                </button>
            </div>
            <div className="tab-container">
                {tab === 'question' && renderQuestionTab()}
                {tab === 'history' && renderHistoryTab()}
                {tab === 'chart' && (
                    <PerformanceChart
                        responses={responses}
                        currentWeek={currentWeek}
                        setCurrentWeek={setCurrentWeek}
                    />
                )}
            </div>
        </div>
    );
};

export default PerformanceQuestion;
